import { useState, useEffect, useRef, useCallback } from 'react';

type MicLevelStatus = 'low' | 'good' | 'high';

interface PushToTalkSettingsProps {
  pttHotkeyDisplay: string;
  onPttHotkeyChange: (code: string, display: string) => void;
  isLive: boolean;
  liveAudioLevel: number;
  isPttActive: boolean;
  selectedMicId: string;
  onMicChange: (deviceId: string) => void;
  inputGain: number;
  onInputGainChange: (gain: number) => void;
  loopbackEnabled: boolean;
  onLoopbackEnabledChange: (enabled: boolean) => void;
}

export function PushToTalkSettings({
  pttHotkeyDisplay,
  onPttHotkeyChange,
  isLive,
  liveAudioLevel,
  isPttActive,
  selectedMicId,
  onMicChange,
  inputGain,
  onInputGainChange,
  loopbackEnabled,
  onLoopbackEnabledChange,
}: PushToTalkSettingsProps) {
  const [audioDevices, setAudioDevices] = useState<MediaDeviceInfo[]>([]);
  const [micDropdownOpen, setMicDropdownOpen] = useState(false);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [micCheckLevel, setMicCheckLevel] = useState(0);
  const [micCheckError, setMicCheckError] = useState<string | null>(null);
  const micDropdownRef = useRef<HTMLDivElement>(null);
  const micCheckStreamRef = useRef<MediaStream | null>(null);
  const micCheckContextRef = useRef<AudioContext | null>(null);
  const micCheckAnalyserRef = useRef<AnalyserNode | null>(null);
  const micCheckAnimationRef = useRef<number | null>(null);
  const inputGainRef = useRef(inputGain);
  const smoothedDisplayLevelRef = useRef(0);
  const [smoothedMicCheckLevel, setSmoothedMicCheckLevel] = useState(0);
  const [levelStatus, setLevelStatus] = useState<MicLevelStatus>('low');

  useEffect(() => {
    inputGainRef.current = inputGain;
  }, [inputGain]);

  const stopMicCheck = useCallback(() => {
    if (micCheckAnimationRef.current !== null) {
      cancelAnimationFrame(micCheckAnimationRef.current);
      micCheckAnimationRef.current = null;
    }

    if (micCheckContextRef.current) {
      micCheckContextRef.current.close().catch(() => {});
      micCheckContextRef.current = null;
    }

    if (micCheckStreamRef.current) {
      micCheckStreamRef.current.getTracks().forEach((track) => track.stop());
      micCheckStreamRef.current = null;
    }

    micCheckAnalyserRef.current = null;
    setMicCheckLevel(0);
  }, []);

  const startMicCheck = useCallback(async () => {
    stopMicCheck();
    setMicCheckError(null);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: selectedMicId ? { deviceId: { exact: selectedMicId } } : true,
      });

      const context = new AudioContext();
      if (context.state === 'suspended') {
        await context.resume();
      }

      const source = context.createMediaStreamSource(stream);
      const analyser = context.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.85;

      source.connect(analyser);

      micCheckStreamRef.current = stream;
      micCheckContextRef.current = context;
      micCheckAnalyserRef.current = analyser;

      const samples = new Uint8Array(analyser.fftSize);
      const updateLevel = () => {
        if (!micCheckAnalyserRef.current) {
          return;
        }

        micCheckAnalyserRef.current.getByteTimeDomainData(samples);
        let sumSquares = 0;
        for (let i = 0; i < samples.length; i += 1) {
          const normalized = (samples[i] - 128) / 128;
          sumSquares += normalized * normalized;
        }

        const rms = Math.sqrt(sumSquares / samples.length);
        const boostedLevel = Math.min(100, rms * 260 * Math.max(inputGainRef.current, 0.3));
        setMicCheckLevel(boostedLevel);
        micCheckAnimationRef.current = requestAnimationFrame(updateLevel);
      };

      micCheckAnimationRef.current = requestAnimationFrame(updateLevel);
    } catch {
      setMicCheckError('Could not access your microphone. Check permissions and try again.');
      setMicCheckLevel(0);
    }
  }, [selectedMicId, stopMicCheck]);

  const meterColorClass = levelStatus === 'high' ? 'bg-red-500' : levelStatus === 'good' ? 'bg-green-400' : 'bg-yellow-400';
  const displayLevel = isLive
    ? Math.min(100, Math.max(0, liveAudioLevel * 100))
    : smoothedMicCheckLevel;

  useEffect(() => {
    const targetLevel = Math.min(100, Math.max(0, isLive ? liveAudioLevel * 100 : micCheckLevel));
    if (isLive) {
      smoothedDisplayLevelRef.current = targetLevel;
      setLevelStatus((previousStatus) => {
        if (previousStatus === 'high') {
          return targetLevel > 74 ? 'high' : targetLevel >= 24 ? 'good' : 'low';
        }

        if (previousStatus === 'low') {
          return targetLevel < 26 ? 'low' : targetLevel >= 84 ? 'high' : 'good';
        }

        if (targetLevel >= 88) {
          return 'high';
        }

        if (targetLevel <= 16) {
          return 'low';
        }

        return 'good';
      });
      return;
    }

    const previousLevel = smoothedDisplayLevelRef.current;
    const smoothing = targetLevel > previousLevel ? 0.3 : 0.14;
    const nextLevel = Math.abs(targetLevel) < 0.1 && previousLevel < 0.5
      ? 0
      : previousLevel + (targetLevel - previousLevel) * smoothing;

    smoothedDisplayLevelRef.current = nextLevel;
    setSmoothedMicCheckLevel(nextLevel);
    setLevelStatus((previousStatus) => {
      if (previousStatus === 'high') {
        return nextLevel > 74 ? 'high' : nextLevel >= 24 ? 'good' : 'low';
      }

      if (previousStatus === 'low') {
        return nextLevel < 26 ? 'low' : nextLevel >= 84 ? 'high' : 'good';
      }

      if (nextLevel >= 88) {
        return 'high';
      }

      if (nextLevel <= 16) {
        return 'low';
      }

      return 'good';
    });
  }, [isLive, liveAudioLevel, micCheckLevel]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (micDropdownRef.current && !micDropdownRef.current.contains(event.target as Node)) {
        setMicDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    async function getDevices() {
      let permissionStream: MediaStream | null = null;
      try {
        permissionStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const devices = await navigator.mediaDevices.enumerateDevices();
        setAudioDevices(devices.filter((d) => d.kind === 'audioinput'));
      } catch {
        setAudioDevices([]);
      } finally {
        permissionStream?.getTracks().forEach((track) => track.stop());
      }
    }

    void getDevices();
    const deviceChangeHandler = () => void getDevices();
    navigator.mediaDevices.addEventListener('devicechange', deviceChangeHandler);
    return () => navigator.mediaDevices.removeEventListener('devicechange', deviceChangeHandler);
  }, []);

  useEffect(() => {
    if (isLive) {
      stopMicCheck();
      return;
    }

    startMicCheck().catch(() => {});
    return () => {
      stopMicCheck();
    };
  }, [isLive, startMicCheck, stopMicCheck]);

  return (
    <div className="space-y-3">
      <section className="relative overflow-hidden rounded-[24px] border border-white/10 bg-[linear-gradient(135deg,rgba(15,23,42,0.92),rgba(10,26,40,0.86))] p-4">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_12%_0%,rgba(34,211,238,0.14),transparent_32%),radial-gradient(circle_at_90%_40%,rgba(168,85,247,0.12),transparent_30%)]" />
        <div className="relative flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <div className="text-[10px] font-black uppercase tracking-[0.24em] text-cyan-100/[0.45]">Audio Control</div>
            <h3 className="mt-2 text-xl font-black tracking-[-0.03em] text-white">Mic, listening mode, and monitoring</h3>
            <p className="mt-1 text-sm text-slate-300/[0.62]">Set how StreamBuddy hears you before and during stream.</p>
          </div>
          <div className="grid grid-cols-3 gap-2 rounded-[18px] border border-white/10 bg-black/[0.22] p-2 text-center">
            <div className="px-3 py-2">
              <div className="text-[9px] font-black uppercase tracking-[0.18em] text-white/[0.35]">Mode</div>
              <div className="mt-1 text-xs font-black text-white">PTT</div>
            </div>
            <div className="px-3 py-2">
              <div className="text-[9px] font-black uppercase tracking-[0.18em] text-white/[0.35]">Mic</div>
              <div className="mt-1 text-xs font-black text-white">{selectedMicId ? 'Selected' : 'Auto'}</div>
            </div>
            <div className="px-3 py-2">
              <div className="text-[9px] font-black uppercase tracking-[0.18em] text-white/[0.35]">Level</div>
              <div className={`mt-1 text-xs font-black ${levelStatus === 'good' ? 'text-emerald-300' : levelStatus === 'high' ? 'text-red-300' : 'text-amber-300'}`}>{levelStatus}</div>
            </div>
          </div>
        </div>
      </section>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="rounded-[24px] border border-white/10 bg-[linear-gradient(145deg,rgba(15,23,42,0.84),rgba(12,18,32,0.92))] p-4">
          <div className="flex flex-col gap-3 xl:flex-row xl:items-start xl:justify-between">
            <div>
              <h3 className="text-lg font-black text-white">Listening Mode</h3>
              <p className="mt-1 text-sm text-slate-300/[0.58]">Push-to-talk only. Hold your talk key to speak.</p>
            </div>
            {isLive && (
              <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${isPttActive ? 'border-cyan-300/30 bg-cyan-400/10 text-cyan-200' : 'border-white/10 bg-white/[0.04] text-slate-300/[0.55]'}`}>
                {isPttActive ? 'Listening now' : 'Idle'}
              </span>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-3 rounded-[18px] border border-white/10 bg-black/[0.18] p-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-black text-white">Talk key</p>
              <p className="mt-1 text-xs text-slate-300/[0.52]">Click the key and press a replacement.</p>
            </div>
            <button
              type="button"
              onClick={() => {
                const handler = (e: KeyboardEvent) => {
                  e.preventDefault();
                  const display = e.key === ' ' ? 'Space' : e.key.length === 1 ? e.key : e.code.replace('Key', '');
                  onPttHotkeyChange(e.code, display);
                  window.removeEventListener('keydown', handler);
                };
                window.addEventListener('keydown', handler, { once: true });
              }}
              className="min-w-[132px] rounded-[16px] border border-cyan-300/[0.24] bg-cyan-400/10 px-4 py-3 text-sm font-black text-white transition-colors hover:border-cyan-200/40"
            >
              {pttHotkeyDisplay}
            </button>
          </div>
        </section>

        <section className="rounded-[24px] border border-white/10 bg-black/[0.18] p-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-black text-white">Mic Check</h3>
              <p className="mt-1 text-xs text-slate-300/[0.55]">Talk normally. Aim for green.</p>
            </div>
            <span className={`rounded-full border px-3 py-1 text-[10px] font-black uppercase tracking-[0.18em] ${levelStatus === 'good' ? 'border-emerald-300/[0.24] bg-emerald-400/10 text-emerald-200' : levelStatus === 'high' ? 'border-red-300/[0.24] bg-red-400/10 text-red-200' : 'border-amber-300/[0.24] bg-amber-400/10 text-amber-200'}`}>
              {levelStatus}
            </span>
          </div>

          <div className="mt-5 rounded-[18px] border border-white/10 bg-black/[0.24] p-4">
            <div className="h-3 overflow-hidden rounded-full border border-white/10 bg-black/[0.32] p-[2px]">
              <div
                className={`h-full rounded-full transition-all duration-75 ${meterColorClass}`}
                style={{ width: `${displayLevel}%` }}
              />
            </div>
            <p className="mt-3 text-xs text-slate-300/[0.62]">
              {levelStatus === 'high'
                ? 'Too loud. Lower mic level in Advanced.'
                : levelStatus === 'good'
                  ? 'Looks good.'
                  : 'Too quiet. Raise mic level in Advanced.'}
            </p>
            {isLive && <p className="mt-1 text-[10px] text-slate-400">Using live meter from the active session.</p>}
            {!isLive && !micCheckError && <p className="mt-1 text-[10px] text-slate-400">Mic check runs while this page is open.</p>}
            {micCheckError && (
              <div className="mt-3 flex items-center gap-3">
                <p className="text-[11px] text-red-300">{micCheckError}</p>
                <button
                  type="button"
                  onClick={() => void startMicCheck()}
                  className="rounded-lg border border-white/10 bg-white/[0.05] px-3 py-2 text-[10px] font-black uppercase tracking-widest text-slate-300 hover:bg-white/[0.08]"
                >
                  Retry
                </button>
              </div>
            )}
          </div>
        </section>
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_320px]">
        <section className="rounded-[24px] border border-white/10 bg-black/[0.18] p-4">
        <div className="flex items-center justify-between">
          <div>
              <h3 className="text-lg font-black text-white">Microphone</h3>
              <p className="mt-1 text-sm text-slate-300/[0.56]">Auto works for most streamers. Pick a device if you need one.</p>
          </div>
        </div>

        <div ref={micDropdownRef} className="relative">
          <button
            type="button"
            onClick={() => setMicDropdownOpen(!micDropdownOpen)}
              className="mt-3 flex w-full items-center justify-between rounded-[16px] border border-white/10 bg-black/[0.28] px-4 py-3 text-sm text-white transition-colors hover:border-cyan-300/[0.35] focus:outline-none focus:border-cyan-300/[0.35]"
          >
            <span className="truncate">
              {selectedMicId
                ? (audioDevices.find((d) => d.deviceId === selectedMicId)?.label || `Microphone ${selectedMicId.slice(0, 8)}`)
                : 'Auto (Recommended)'}
            </span>
            <svg className={`w-4 h-4 text-gray-400 transition-transform ${micDropdownOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>

          {micDropdownOpen && (
              <div className="absolute z-50 mt-2 max-h-64 w-full overflow-y-auto rounded-[16px] border border-white/10 bg-[#0a0a0f] shadow-2xl shadow-black/80">
              <button
                type="button"
                onClick={() => {
                  onMicChange('');
                  setMicDropdownOpen(false);
                }}
                  className={`w-full px-4 py-3 text-left text-sm transition-colors hover:bg-cyan-500/10 ${selectedMicId === '' ? 'bg-cyan-500/20 text-cyan-200 font-bold' : 'text-gray-400 hover:text-white'}`}
              >
                Auto (Recommended)
              </button>
              {audioDevices.map((device) => (
                <button
                  type="button"
                  key={device.deviceId}
                  onClick={() => {
                    onMicChange(device.deviceId);
                    setMicDropdownOpen(false);
                  }}
                    className={`w-full truncate px-4 py-3 text-left text-sm transition-colors hover:bg-cyan-500/10 ${selectedMicId === device.deviceId ? 'bg-cyan-500/20 text-cyan-200 font-bold' : 'text-gray-400 hover:text-white'}`}
                >
                  {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                </button>
              ))}
            </div>
          )}
        </div>
        </section>

        <section className="overflow-hidden rounded-[24px] border border-white/10 bg-black/[0.18]">
        <button
          type="button"
          onClick={() => setAdvancedOpen(!advancedOpen)}
            className="flex w-full items-center justify-between px-4 py-4 text-left"
        >
          <div>
              <p className="text-lg font-black text-white">Advanced</p>
              <p className="mt-1 text-xs text-slate-300/[0.55]">Mic level and monitor controls.</p>
          </div>
            <span className="text-sm text-slate-300/[0.65]">{advancedOpen ? 'Hide' : 'Show'}</span>
        </button>

        {advancedOpen && (
            <div className="space-y-4 border-t border-white/10 px-4 pb-4">
            <div className="pt-4">
              <div className="flex items-center justify-between mb-2">
                  <span className="text-[11px] text-slate-300/[0.62]">Mic level</span>
                  <span className="font-mono text-[11px] text-cyan-200">{inputGain.toFixed(2)}x</span>
              </div>
              <input
                type="range"
                min="0.1"
                max="2.0"
                step="0.05"
                value={inputGain}
                onChange={(e) => onInputGainChange(parseFloat(e.target.value))}
                  className="h-1.5 w-full cursor-pointer appearance-none rounded-lg bg-black/[0.4] accent-cyan-400"
              />
            </div>

              <div className="flex items-center justify-between rounded-[16px] border border-white/10 bg-white/[0.045] p-3">
              <div>
                  <p className="text-xs font-bold text-white">Monitor input</p>
                  <p className="text-[11px] text-slate-300/[0.55]">Hear your mic for quick checks.</p>
              </div>
              <button
                  type="button"
                onClick={() => onLoopbackEnabledChange(!loopbackEnabled)}
                  className={`relative h-6 w-11 rounded-full transition-colors ${loopbackEnabled ? 'bg-cyan-500' : 'bg-slate-700'}`}
              >
                  <div className={`absolute top-1 h-4 w-4 rounded-full bg-white transition-transform ${loopbackEnabled ? 'left-6' : 'left-1'}`} />
              </button>
            </div>
          </div>
        )}
        </section>
      </div>
    </div>
  );
}
